import { useEffect } from "react";
import { Microphone, MicrophoneSlash, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useVoiceDictation } from "./useVoiceDictation";

/**
 * MicButton — Vague 7.
 *
 * A compact microphone toggle that dictates FR speech directly into the
 * parent's note draft via the `onAppend` callback (which receives every
 * finalised chunk of text).
 *
 * Props :
 *   onAppend(text)  — called each time a final chunk is recognised
 *   testid          — data-testid for the button
 *   className       — extra classes for positioning
 */
export function MicButton({ onAppend, testid = "mic-btn", className = "" }) {
  const {
    isSupported, isListening, interim, error, start, stop, reset,
  } = useVoiceDictation({
    lang: "fr-FR",
    continuous: true,
    interimResults: true,
    onFinal: (chunk) => onAppend?.(chunk),
  });

  useEffect(() => {
    if (error) {
      toast.error(error);
      reset();
    }
  }, [error, reset]);

  if (!isSupported) {
    // Hide silently — no point showing a disabled button to Firefox users
    return null;
  }

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isListening) stop();
    else start();
  };

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testid}
      className={`inline-flex items-center justify-center rounded-sm border transition-colors min-h-[28px] min-w-[28px] ${
        isListening
          ? "bg-secondary text-secondary-foreground border-secondary animate-pulse"
          : "bg-muted/40 text-muted-foreground border-border hover:bg-primary/10 hover:text-primary"
      } ${className}`}
      title={isListening ? "Arrêter la dictée" : "Dicter la note (parle en français)"}
      aria-label={isListening ? "Arrêter la dictée" : "Dicter la note"}
      aria-pressed={isListening}
    >
      {isListening ? (
        <Stop size={14} weight="fill"/>
      ) : (
        <Microphone size={14} weight="regular"/>
      )}
      {isListening && interim && (
        <span className="ml-1 text-[10px] font-mono text-secondary-foreground/90 truncate max-w-[110px] hidden sm:inline">
          {interim}
        </span>
      )}
    </button>
  );
}
